import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import { LoadingScreen } from '@/components/layout/LoadingScreen';
import { requiresMfa } from '@/lib/permissions';

interface RequireMfaProps {
  children: ReactNode;
}

/**
 * Gate that sits *inside* RequireAuth on every financials surface. By the
 * time we get here we know:
 *   - we have a session,
 *   - we have a profile,
 *   - the role passed RequireAuth's module check (e.g. canRead 'financials').
 *
 * What we still need to enforce:
 *   - the role is one we require MFA for (defense-in-depth — should be
 *     true by definition because the role passed `module=financials`),
 *   - a verified phone factor exists on the user,
 *   - the *current session* is at AAL2 (not just AAL1 with a factor on
 *     file).
 *
 * Failure modes redirect the user instead of just rendering /forbidden,
 * so this gate doubles as the user-facing "please verify" prompt.
 *
 * Ordering note: this MUST be wrapped in RequireAuth, never the other
 * way around. RequireMfa assumes `profile` is non-null.
 */
export function RequireMfa({ children }: RequireMfaProps) {
  const { profile, mfa, loading } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingScreen />;

  // Belt-and-suspenders: if RequireAuth wasn't wrapped around us
  // somehow, fail closed instead of letting an unauthenticated render
  // through.
  if (!profile) return <Navigate to="/auth/sign-in" replace />;

  // If the role doesn't require MFA, this gate is a no-op. We still
  // wrap financials routes in RequireMfa for defense-in-depth, so a
  // future relaxation that gives, say, `manager` financials access
  // doesn't accidentally also bypass MFA.
  if (!requiresMfa(profile.role)) return <>{children}</>;

  // No verified factor at all -> force enrollment.
  if (mfa.verifiedPhoneFactors.length === 0) {
    return (
      <Navigate
        to="/auth/mfa/setup"
        state={{ from: location }}
        replace
      />
    );
  }

  // Factor exists, session not yet AAL2 -> challenge.
  if (mfa.currentLevel !== 'aal2') {
    return (
      <Navigate
        to="/auth/mfa/challenge"
        state={{ from: location }}
        replace
      />
    );
  }

  return <>{children}</>;
}
